package dev.lingvo.site.components

import dev.lingvo.shared.MtProvider

/**
 * Path data for each provider's real mark, so [ProviderMark] can draw the vendor's own silhouette
 * instead of a monogram.
 *
 * Sources:
 * - Claude, OpenAI, DeepL, Google Translate and LibreTranslate: [Simple Icons](https://simpleicons.org)
 *   16.30.0, licensed [CC0-1.0](https://creativecommons.org/publicdomain/zero/1.0/). Each icon is a
 *   single `<path d>` in a 24x24 `viewBox`, used verbatim (long path strings are wrapped across
 *   multiple Kotlin string literals purely for line length; joined, they reproduce the source
 *   attribute exactly).
 * - MyMemory: not a Simple Icons entry, so this is the icon portion of
 *   [MyMemory's own published logo](https://mymemory.translated.net/public/img/mym_logo_horizontal.svg)
 *   — the circular mark that sits to the left of the wordmark, extracted as its own 60x60 `viewBox`
 *   without altering the path shapes.
 */
internal class ProviderGlyph(val viewBox: Int, val paths: List<String>)

internal fun providerGlyph(provider: MtProvider): ProviderGlyph = when (provider) {
    MtProvider.CLAUDE -> ProviderGlyph(
        viewBox = 24,
        paths = listOf(
            "m4.7144 15.9555 " +
                "4.7174-2.6471.079-.2307-.079-.1275h-.2307l-.7893-.0486-2.6956-.0729-2.3375-.0971-2.2646-.1214-.5707-.1215-.5343-.7042.0546-.3522.4797-.3218.686.0608 " +
                "1.5179.1032 2.2767.1578 1.6514.0972 2.4468.255h.3886l.0546-.1579-.1336-.0971-.1032-.0972L6.973 " +
                "9.8356l-2.55-1.6879-1.3356-.9714-.7225-.4918-.3643-.4614-.1578-1.0078.6557-.7225.8803.0607.2246.0607.8925.686 " +
                "1.9064 1.4754 2.4893 " +
                "1.8336.3643.3035.1457-.1032.0182-.0728-.164-.2733-1.3539-2.4467-1.445-2.4893-.6435-1.032-.17-.6194c-.0607-.255-.1032-.4674-.1032-.7285L6.287.1335 " +
                "6.6997 0l.9957.1336.419.3642.6192 1.4147 1.0018 2.2282 1.5543 " +
                "3.0296.4553.8985.2429.8318.091.255h.1579v-.1457l.1275-1.706.2368-2.0947.2307-2.6957.0789-.7589.3764-.9107.7468-.4918.5828.2793.4797.686-.0668.4433-.2853 " +
                "1.8517-.5586 2.9021-.3643 1.9429h.2125l.2429-.2429.9835-1.3053 " +
                "1.6514-2.0643.7286-.8196.85-.9046.5464-.4311h1.0321l.759 1.1293-.34 1.1657-1.0625 1.3478-.8804 " +
                "1.1414-1.2628 1.7-.7893 1.36.0729.1093.1882-.0183 2.8535-.607 1.5421-.2794 " +
                "1.8396-.3157.8318.3886.091.3946-.3278.8075-1.967.4857-2.3072.4614-3.4364.8136-.0425.0304.0486.0607 " +
                "1.5482.1457.6618.0364h1.621l3.0175.2247.7892.522.4736.6376-.079.4857-1.2142.6193-1.6393-.3886-3.825-.9107-1.3113-.3279h-.1822v.1093l1.0929 " +
                "1.0686 2.0035 1.8092 2.5075 " +
                "2.3314.1275.5768-.3218.4554-.34-.0486-2.2039-1.6575-.85-.7468-1.9246-1.621h-.1275v.17l.4432.6496 " +
                "2.3436 3.5214.1214 1.0807-.17.3521-.6071.2125-.6679-.1214-1.3721-1.9246L14.38 " +
                "17.959l-1.1414-1.9428-.1397.079-.674 " +
                "7.2552-.3156.3703-.7286.2793-.6071-.4614-.3218-.7468.3218-1.4753.3886-1.9246.3157-1.53.2853-1.9004.17-.6314-.0121-.0425-.1397.0182-1.4328 " +
                "1.9672-2.1796 2.9446-1.7243 1.8456-.4128.164-.7164-.3704.0667-.6618.4008-.5889 2.386-3.0357 " +
                "1.4389-1.882.929-1.0868-.0062-.1579h-.0546l-6.3385 " +
                "4.1164-1.1293.1457-.4857-.4554.0608-.7467.2307-.2429 1.9064-1.3114Z",
        ),
    )

    MtProvider.OPENAI -> ProviderGlyph(
        viewBox = 24,
        paths = listOf(
            "M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 " +
                "0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 " +
                "0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 " +
                "0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 " +
                "12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 " +
                ".3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 " +
                "4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 " +
                ".7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 " +
                "1-6.1408-1.6464zM2.3408 7.8956a4.485 4.485 0 0 1 2.3655-1.9728V11.6a.7664.7664 0 0 0 " +
                ".3879.6765l5.8144 3.3543-2.0201 1.1685a.0757.0757 0 0 1-.071 0l-4.8303-2.7865A4.504 4.504 0 0 1 " +
                "2.3408 7.872zm16.5963 3.8558L13.1038 8.364 15.1192 7.2a.0757.0757 0 0 1 .071 0l4.8303 " +
                "2.7913a4.4944 4.4944 0 0 1-.6765 8.1042v-5.6772a.79.79 0 0 " +
                "0-.407-.667zm2.0107-3.0231l-.142-.0852-4.7735-2.7818a.7759.7759 0 0 0-.7854 0L9.409 " +
                "9.2297V6.8974a.0662.0662 0 0 1 .0284-.0615l4.8303-2.7866a4.4992 4.4992 0 0 1 6.6802 " +
                "4.66zM8.3065 12.863l-2.02-1.1638a.0804.0804 0 0 1-.038-.0567V6.0742a4.4992 4.4992 0 0 1 " +
                "7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 " +
                "2.6069 1.4998v2.9994l-2.5974 1.4997-2.6067-1.4997Z",
        ),
    )

    MtProvider.DEEPL -> ProviderGlyph(
        viewBox = 24,
        paths = listOf(
            "M20.907 4.93953 12.68543.18573a1.3577 1.3577 0 0 0-1.3709 0L3.09298 4.9565a1.3766 1.3766 0 0 " +
                "0-.68639 1.18233v9.52646a1.3766 1.3766 0 0 0 .68639 1.19363l8.22157 4.75946.06223.03583 4.04856 " +
                "2.3458-.01131-2.06106.0075-1.1446.0038.01885v-.38467c0-.23006.1188-.43371.29605-.56005l.264-.15086.12633-.06977h-.0075l4.80283-2.7795a1.3803 " +
                "1.3803 0 0 0 .68639-1.19551V6.13505a1.3803 1.3803 0 0 0-.68642-1.19552m-9.85269 9.68863a1.4275 " +
                "1.4275 0 0 1-.39976 1.3841 1.4086 1.4086 0 0 1-1.97054 0 1.4199 1.4199 0 0 1 0-2.06294 1.4086 " +
                "1.4086 0 0 1 2.0422.07543l3.32822-1.91585.6864.38656zm5.77019-2.41367a1.4086 1.4086 0 0 " +
                "1-1.97054 0 1.4256 1.4256 0 0 1-.3696-1.47837l-.0132.0075-3.7525-2.1723-.05657.05656a1.4086 " +
                "1.4086 0 0 1-1.97053 0 1.4199 1.4199 0 0 1 0-2.06293 1.4086 1.4086 0 0 1 1.97242 " +
                "0c.3941.37713.52422.91832.39033 1.40672l3.7808 2.20059.01886-.01886a1.4086 1.4086 0 0 1 1.97242 " +
                "0 1.42746 1.42746 0 0 1 0 2.06105z",
        ),
    )

    MtProvider.GOOGLE -> ProviderGlyph(
        viewBox = 24,
        paths = listOf(
            "M22.401 4.818h-9.927L10.927 0H1.599C.72 0 .002.719.002 1.599v16.275c0 .878.72 1.597 1.597 " +
                "1.597h10L13.072 24H22.4c.878 0 1.597-.707 1.597-1.572V6.39c0-.865-.72-1.572-1.597-1.572zm-15.66 " +
                "8.68c-2.07 0-3.75-1.68-3.75-3.75 0-2.07 1.68-3.75 3.75-3.75 1.012 0 1.86.375 " +
                "2.512.976l-.99.952a2.194 2.194 0 0 0-1.522-.584c-1.305 0-2.363 1.08-2.363 2.409S5.436 12.16 " +
                "6.74 12.16c1.507 0 2.13-1.08 2.19-1.808l-2.188-.002V9.066h3.51c.05.23.09.457.09.764 0 " +
                "2.147-1.434 3.669-3.602 3.669zm16.757 8.93c0 .59-.492 1.072-1.097 " +
                "1.072h-8.875l3.649-4.03h.005l-.74-2.302.006-.005s.568-.488 1.277-1.24c.712.771 1.63 1.699 2.818 " +
                "2.805l.771-.772c-1.272-1.154-2.204-2.07-2.89-2.805.919-1.087 1.852-2.455 " +
                "2.049-3.707h2.034v.002h.002v-.94h-4.532v-1.52h-1.471v1.52H14.3l-1.672-5.21.006.022h9.767c.605 0 " +
                "1.097.48 1.097 " +
                "1.072v16.038zm-6.484-7.311c-.536.548-.943.873-.943.873l-.008.004-1.46-4.548h4.764c-.307 " +
                "1.084-.988 2.108-1.651 2.904-1.176-1.392-1.18-1.844-1.18-1.844h-1.222s.05.678 1.7 2.61z",
        ),
    )

    MtProvider.LIBRETRANSLATE -> ProviderGlyph(
        viewBox = 24,
        paths = listOf(
            "M13.7835 0q-1.7162 4.666-8.892 7.991l1.3623 1.523q5.3951-2.6385 7.8192-5.996 2.4777 3.1857 " +
                "8.1948 6.1247L23.544 8.066q-6.103-2.9283-8.4198-6.2425.118-.1824.4827-1.255ZM9.5467 " +
                "7.991q-.3218 3.5075-1.4481 5.8028-1.1155 2.2953-3.6683 4.5692l1.4265 1.2763q2.8426-2.6064 " +
                "4.1941-5.556 1.1799 1.0297 2.4133 " +
                "2.6494l1.4588-1.3086q-1.1477-1.4587-3.2286-3.25.547-2.0271.7614-3.9793zm7.1007 0q-.1716 " +
                "4.151-1.4158 6.3927-1.2444 2.2417-3.7328 3.8934l1.4051 1.298q3.5718-2.6065 4.7088-5.8242 1.7268 " +
                "3.4644 4.8696 " +
                "5.867l1.4051-1.3408q-1.7806-1.3192-3.0141-2.7887-1.2227-1.4801-1.8662-3.0461-.6328-1.5767-.6328-2.1452l.075-.751q.0859-.8366.0859-1.3942zM.1126 " +
                "8.8018V24h9.4443v-1.7095H2.0515V8.8018Z",
        ),
    )

    MtProvider.MYMEMORY -> ProviderGlyph(
        viewBox = 60,
        paths = listOf(
            "M30 4.23428C35.096 4.23428 40.0775 5.74542 44.3147 8.57659C48.5518 11.4078 51.8543 15.4318 " +
                "53.8044 20.1399C55.7546 24.848 56.2648 30.0286 55.2706 35.0266C54.2765 40.0247 51.8225 44.6157 " +
                "48.2191 48.2191C44.6157 51.8225 40.0247 54.2764 35.0266 55.2706C30.0286 56.2648 24.848 55.7546 " +
                "20.1399 53.8044C15.4318 51.8543 11.4078 48.5518 8.5766 44.3147C5.74543 40.0775 4.2343 35.096 " +
                "4.2343 30C4.24224 23.1689 6.95938 16.6199 11.7897 11.7897C16.62 6.95937 23.169 4.24222 30 " +
                "4.23428ZM30 0C24.0666 0 18.2664 1.75947 13.3329 5.05591C8.39943 8.35235 4.55426 13.0377 2.28363 " +
                "18.5195C0.0129986 24.0013 -0.581101 30.0333 0.576455 35.8527C1.73401 41.6721 4.59123 47.0176 " +
                "8.78681 51.2132C12.9824 55.4088 18.3279 58.266 24.1473 59.4235C29.9667 60.5811 35.9987 59.987 " +
                "41.4805 57.7164C46.9623 55.4457 51.6476 51.6006 54.9441 46.6671C58.2405 41.7336 60 35.9334 60 " +
                "30C60 22.0435 56.8393 14.4129 51.2132 8.78679C45.5871 3.1607 37.9565 0 30 0Z",
            "M25.2211 42.9474V39.9158H23.7684C21.9684 39.9158 21.3368 38.8421 21.3368 " +
                "37.1684V34.5474C21.3368 32.3053 20.4211 30.6 18.3368 29.9053C20.4211 29.1789 21.3368 27.5053 " +
                "21.3368 25.2316V22.6421C21.3368 20.9684 21.9684 19.8947 23.7684 " +
                "19.8947H25.2211V16.8632H22.5684C19.4421 16.8632 17.2947 18.7263 17.2947 22.1053V25.9895C17.2947 " +
                "27.3474 16.8526 28.3895 15.1158 28.3895H13.6V31.4211H15.1158C16.8526 31.4211 17.2947 32.4632 " +
                "17.2947 33.8211V37.7053C17.2947 41.0842 19.4421 42.9474 22.5684 42.9474H25.2211Z",
            "M45.9102 31.4211V28.3895H44.426C42.6891 28.3895 42.2155 27.3789 42.2155 25.9895V22.1053C42.2155 " +
                "18.7263 40.0997 16.8632 36.9734 16.8632H34.2891V19.8947H35.7418C37.5734 19.8947 38.1734 20.9684 " +
                "38.1734 22.6421V25.2632C38.1734 27.5053 39.1207 29.2105 41.1734 29.9053C39.1207 30.6316 38.1734 " +
                "32.3053 38.1734 34.5789V37.1684C38.1734 38.8421 37.5734 39.9158 35.7418 " +
                "39.9158H34.2891V42.9474H36.9734C40.0997 42.9474 42.2155 41.0842 42.2155 37.7053V33.8211C42.2155 " +
                "32.4632 42.6891 31.4211 44.426 31.4211H45.9102Z",
        ),
    )
}
